/**
 * @fileoverview Company View Toggle - Alterna visualização entre empresas
 * 
 * Permite ao usuário escolher entre:
 * - Consolidado: Sétima + 555 Studios (padrão)
 * - Sétima: Apenas dados da Sétima
 * - 555 Studios: Apenas dados do 555
 * - Comparação: Lado a lado
 * 
 * @example
 * ```tsx
 * <CompanyViewToggle
 *   value={viewMode}
 *   onChange={(mode) => setViewMode(mode)}
 * />
 * ```
 */

import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business,
  CompareArrows,
  ViewAgenda,
} from '@mui/icons-material';

export type CompanyView = 'consolidated' | 'setima' | '555' | 'comparison';

interface CompanyViewToggleProps {
  value: CompanyView;
  onChange: (view: CompanyView) => void;
  disabled?: boolean;
}

/**
 * Componente de toggle para alternar visualização entre empresas
 */
export const CompanyViewToggle: React.FC<CompanyViewToggleProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: CompanyView | null,
  ) => {
    if (newView !== null) {
      onChange(newView);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 3,
      }}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        disabled={disabled}
        aria-label="visualização por empresa"
        size={isMobile ? 'small' : 'medium'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          '& .MuiToggleButton-root': {
            px: isMobile ? 1.5 : 2.5,
            py: isMobile ? 0.75 : 1,
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            fontWeight: 500,
            textTransform: 'none',
            border: `1px solid ${theme.palette.divider}`,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}
      >
        <Tooltip title="Visualizar dados consolidados (Sétima + 555)" arrow>
          <ToggleButton 
            value="consolidated" 
            aria-label="consolidado"
            data-testid="toggle-consolidado"
          >
            <Business sx={{ mr: isMobile ? 0 : 0.5, fontSize: isMobile ? '1rem' : '1.25rem' }} />
            {!isMobile && 'Consolidado'}
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Visualizar apenas dados da Sétima" arrow>
          <ToggleButton 
            value="setima" 
            aria-label="sétima"
            data-testid="toggle-setima"
          >
            <Box
              component="span"
              sx={{
                fontWeight: 'bold',
                fontSize: isMobile ? '0.875rem' : '1rem',
                mr: isMobile ? 0 : 0.5,
              }}
            >
              7ª
            </Box>
            {!isMobile && 'Sétima'}
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Visualizar apenas dados do 555 Studios" arrow>
          <ToggleButton 
            value="555" 
            aria-label="555 studios"
            data-testid="toggle-555"
          >
            <Box
              component="span"
              sx={{
                fontWeight: 'bold',
                fontSize: isMobile ? '0.875rem' : '1rem',
                mr: isMobile ? 0 : 0.5,
              }}
            >
              555
            </Box>
            {!isMobile && 'Studios'}
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Comparar Sétima e 555 lado a lado" arrow>
          <ToggleButton 
            value="comparison" 
            aria-label="comparação"
            data-testid="toggle-comparacao"
          >
            <CompareArrows sx={{ mr: isMobile ? 0 : 0.5, fontSize: isMobile ? '1rem' : '1.25rem' }} />
            {!isMobile && 'Comparar'}
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      {/* Indicador visual adicional */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          ml: 'auto',
          color: theme.palette.text.secondary,
          fontSize: '0.75rem',
        }}
      >
        <ViewAgenda sx={{ fontSize: '1rem' }} />
        {!isMobile && (
          <span>
            {value === 'consolidated' && 'Visualização: Consolidada'}
            {value === 'setima' && 'Visualização: Sétima'}
            {value === '555' && 'Visualização: 555 Studios'}
            {value === 'comparison' && 'Visualização: Comparativa'}
          </span>
        )}
      </Box>
    </Box>
  );
};


